/**
 * Centralized Role and Permission Constants
 * KISS Architecture - Single source of truth for all role-related constants
 */

import { getRoleColor } from '../assets/theme/roleColors';
import { TERMINOLOGY } from './terminology';

// =============================================================================
// ROLE DEFINITIONS
// =============================================================================

export const USER_ROLES = {
  CARE_OWNER: 'care_owner',        // Exactly one per child (main responsible person)
  CARE_PARTNER: 'care_partner',    // Family members, relatives, friends
  CAREGIVER: 'caregiver',          // Professional helpers (nannies, aides, teachers)  
  THERAPIST: 'therapist'           // Professional advisors (speech, OT, behavioral)
};

// Role display information - now uses centralized role colors
export const ROLE_DISPLAY = {
  [USER_ROLES.CARE_OWNER]: {
    plainLabel: 'Care Owner',
    label: 'Care Owner',
    description: `Main responsible person - Full control of the ${TERMINOLOGY.subjectLower} and ${TERMINOLOGY.careCircleLower}`,
    badge: 'Full Control',
    inviteLabel: `Full control of the ${TERMINOLOGY.subjectLower} and ${TERMINOLOGY.careCircleLower}`,
    dashboardHint: `Can manage team and ${TERMINOLOGY.subjectLower} records`,
    teamViewHint: `You can invite, remove, and manage everyone on this ${TERMINOLOGY.subjectLower}'s ${TERMINOLOGY.careCircleLower}.`,
    color: getRoleColor('careOwner', 'primary'), // Royal Blue #2C74D4
    priority: 1, // Highest priority
    icon: null
  },
  [USER_ROLES.CARE_PARTNER]: {
    plainLabel: 'Care Partner',
    label: 'Care Partner',
    description: `Trusted co-manager who can help manage the ${TERMINOLOGY.subjectLower} and ${TERMINOLOGY.careCircleLower}`,
    badge: 'Co-Manager',
    inviteLabel: `Can help manage the ${TERMINOLOGY.subjectLower}, care details, and ${TERMINOLOGY.careCircleLower}`,
    dashboardHint: `Can help manage the ${TERMINOLOGY.subjectLower}, care details, and ${TERMINOLOGY.careCircleLower}`,
    teamViewHint: 'You can help manage this care profile and care circle. Only the Care Owner can delete the profile or transfer ownership.',
    color: getRoleColor('carePartner', 'primary'), // Violet #8B5CF6
    priority: 2,
    icon: null
  },
  [USER_ROLES.CAREGIVER]: {
    plainLabel: 'Caregiver',
    label: 'Caregiver',
    description: 'Professional helper - Limited access to shared information',
    badge: 'Restricted Access',
    inviteLabel: 'Can add daily logs and view shared updates',
    dashboardHint: 'Can log shared daily updates',
    teamViewHint: 'Caregivers can log shared updates, but only Care Owners and Care Partners can add or remove team members.',
    color: getRoleColor('caregiver', 'primary'), // Emerald Green #059669
    priority: 3,
    icon: null
  },
  [USER_ROLES.THERAPIST]: {
    plainLabel: 'Therapist',
    label: 'Therapist',
    description: 'Professional advisor - View all + add professional notes',
    badge: 'Professional View',
    inviteLabel: 'Can view shared information and add professional notes',
    dashboardHint: 'Can add professional notes',
    teamViewHint: 'Therapists can view shared information and add professional notes, but cannot manage the team.',
    color: getRoleColor('therapist', 'primary'), // Teal #0891B2
    priority: 4,
    icon: null
  }
};

// =============================================================================
// PERMISSION DEFINITIONS
// =============================================================================

export const PERMISSIONS = {
  // Child Management
  CREATE_CHILD: 'create_child',
  EDIT_CHILD: 'edit_child',
  DELETE_CHILD: 'delete_child',
  MANAGE_SETTINGS: 'manage_settings',
  MANAGE_PRIVACY: 'manage_privacy',
  TRANSFER_OWNERSHIP: 'transfer_ownership',
  
  // Team Management
  INVITE_CARE_PARTNER: 'invite_care_partner',
  INVITE_CAREGIVER: 'invite_caregiver', 
  INVITE_THERAPIST: 'invite_therapist',
  REMOVE_TEAM_MEMBERS: 'remove_team_members',
  
  // Log Permissions
  ADD_DAILY_LOGS: 'add_daily_logs',
  ADD_DAILY_HABITS: 'add_daily_habits',
  ADD_PROFESSIONAL_NOTES: 'add_professional_notes',
  VIEW_ALL_LOGS: 'view_all_logs',
  VIEW_SHARED_LOGS_ONLY: 'view_shared_logs_only',
  EDIT_OWN_LOGS: 'edit_own_logs',
  EDIT_ANY_LOGS: 'edit_any_logs',
  DELETE_OWN_LOGS: 'delete_own_logs',
  DELETE_ANY_LOGS: 'delete_any_logs',
  
  // View & Export
  VIEW_ANALYTICS: 'view_analytics',
  VIEW_MEDICAL_INFO: 'view_medical_info',
  EXPORT_REPORTS: 'export_reports'
};

// =============================================================================
// ROLE-PERMISSION MATRIX
// =============================================================================

export const ROLE_PERMISSIONS = {
  [USER_ROLES.CARE_OWNER]: [
    // Full control - exactly one per child
    PERMISSIONS.CREATE_CHILD,
    PERMISSIONS.EDIT_CHILD, 
    PERMISSIONS.DELETE_CHILD,
    PERMISSIONS.MANAGE_SETTINGS,
    PERMISSIONS.MANAGE_PRIVACY,
    PERMISSIONS.TRANSFER_OWNERSHIP,
    PERMISSIONS.INVITE_CARE_PARTNER,
    PERMISSIONS.INVITE_CAREGIVER,
    PERMISSIONS.INVITE_THERAPIST,
    PERMISSIONS.REMOVE_TEAM_MEMBERS,
    PERMISSIONS.ADD_DAILY_LOGS,
    PERMISSIONS.ADD_DAILY_HABITS,
    PERMISSIONS.ADD_PROFESSIONAL_NOTES,
    PERMISSIONS.VIEW_ALL_LOGS,
    PERMISSIONS.EDIT_OWN_LOGS,
    PERMISSIONS.EDIT_ANY_LOGS,
    PERMISSIONS.DELETE_OWN_LOGS,
    PERMISSIONS.DELETE_ANY_LOGS,
    PERMISSIONS.VIEW_ANALYTICS,
    PERMISSIONS.VIEW_MEDICAL_INFO,
    PERMISSIONS.EXPORT_REPORTS
  ],
  
  [USER_ROLES.CARE_PARTNER]: [
    // Trusted co-manager - can manage day-to-day care but not ownership
    PERMISSIONS.EDIT_CHILD,
    PERMISSIONS.MANAGE_SETTINGS,
    PERMISSIONS.MANAGE_PRIVACY,
    PERMISSIONS.INVITE_CAREGIVER,
    PERMISSIONS.INVITE_THERAPIST,
    PERMISSIONS.REMOVE_TEAM_MEMBERS,
    PERMISSIONS.ADD_DAILY_LOGS,
    PERMISSIONS.ADD_DAILY_HABITS,
    PERMISSIONS.VIEW_ALL_LOGS,
    PERMISSIONS.ADD_PROFESSIONAL_NOTES,
    PERMISSIONS.EDIT_OWN_LOGS,
    PERMISSIONS.EDIT_ANY_LOGS,
    PERMISSIONS.DELETE_OWN_LOGS,
    PERMISSIONS.DELETE_ANY_LOGS,
    PERMISSIONS.VIEW_ANALYTICS,
    PERMISSIONS.VIEW_MEDICAL_INFO,
    PERMISSIONS.EXPORT_REPORTS
  ],
  
  [USER_ROLES.CAREGIVER]: [
    // Professional helpers - restricted access
    PERMISSIONS.ADD_DAILY_LOGS,
    PERMISSIONS.ADD_DAILY_HABITS,
    PERMISSIONS.VIEW_SHARED_LOGS_ONLY,
    PERMISSIONS.EDIT_OWN_LOGS,
    PERMISSIONS.DELETE_OWN_LOGS
  ],
  
  [USER_ROLES.THERAPIST]: [
    // Professional advisors - view all + professional notes only
    PERMISSIONS.VIEW_ALL_LOGS,
    PERMISSIONS.ADD_PROFESSIONAL_NOTES, // Only professional notes, no daily logs
    PERMISSIONS.VIEW_ANALYTICS,
    PERMISSIONS.VIEW_MEDICAL_INFO,
    PERMISSIONS.EXPORT_REPORTS
  ]
};

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Check if a role has a specific permission
 * @param {string} role - User role
 * @param {string} permission - Permission to check
 * @returns {boolean} - True if role has permission
 */
export const roleHasPermission = (role, permission) => {
  const permissions = ROLE_PERMISSIONS[role] || [];
  return permissions.includes(permission);
};

export const canEditChildProfile = (role) => (
  role === 'parent' || roleHasPermission(role, PERMISSIONS.EDIT_CHILD)
);

/**
 * Get all permissions for a role
 * @param {string} role - User role
 * @returns {Array} - Array of permissions
 */
export const getRolePermissions = (role) => {
  return ROLE_PERMISSIONS[role] || [];
};

/**
 * Get role display information
 * @param {string} role - User role
 * @returns {Object} - Display information object
 */
export const getRoleDisplay = (role) => {
  return ROLE_DISPLAY[role] || {
    plainLabel: 'Unknown Role',
    label: 'Unknown Role',
    description: 'Unknown role type',
    badge: 'Unknown',
    inviteLabel: 'Unknown access',
    dashboardHint: 'Role information unavailable',
    teamViewHint: 'Role information unavailable.',
    color: '#EF4444',
    priority: 999,
    icon: '❓'
  };
};

/**
 * Get highest priority role (in case user has multiple roles for a child)
 * @param {Array} roles - Array of user roles
 * @returns {string} - Highest priority role
 */
export const getHighestPriorityRole = (roles) => {
  if (!roles || roles.length === 0) return null;
  
  const sortedRoles = roles
    .map(role => ({ role, priority: ROLE_DISPLAY[role]?.priority || 999 }))
    .sort((a, b) => a.priority - b.priority);
    
  return sortedRoles[0]?.role || null;
};

/**
 * Check if a user can invite others (any role)
 * @param {string} userRole - User's role
 * @returns {boolean} - True if user can invite team members
 */
export const canInviteTeamMembers = (userRole) => {
  return roleHasPermission(userRole, PERMISSIONS.INVITE_CARE_PARTNER) ||
         roleHasPermission(userRole, PERMISSIONS.INVITE_CAREGIVER) ||
         roleHasPermission(userRole, PERMISSIONS.INVITE_THERAPIST);
};

export const canInviteCarePartner = (userRole) =>
  roleHasPermission(userRole, PERMISSIONS.INVITE_CARE_PARTNER);

export const canInviteCaregiver = (userRole) =>
  roleHasPermission(userRole, PERMISSIONS.INVITE_CAREGIVER);

export const canInviteTherapist = (userRole) =>
  roleHasPermission(userRole, PERMISSIONS.INVITE_THERAPIST);

export const canInviteAnyone = (userRole) =>
  canInviteTeamMembers(userRole);

export const canManageTeam = (userRole) =>
  [USER_ROLES.CARE_OWNER, USER_ROLES.CARE_PARTNER].includes(userRole);

export const canRemoveTeamMembers = (userRole) =>
  roleHasPermission(userRole, PERMISSIONS.REMOVE_TEAM_MEMBERS);

export const canTransferOwnership = (userRole) =>
  roleHasPermission(userRole, PERMISSIONS.TRANSFER_OWNERSHIP);

/**
 * Get invitation options available to a user role
 * @param {string} userRole - User's role
 * @returns {Array} - Array of role types they can invite
 */
export const getInvitationOptions = (userRole) => {
  const options = [];
  
  if (roleHasPermission(userRole, PERMISSIONS.INVITE_CARE_PARTNER)) {
    options.push(USER_ROLES.CARE_PARTNER);
  }
  if (roleHasPermission(userRole, PERMISSIONS.INVITE_CAREGIVER)) {
    options.push(USER_ROLES.CAREGIVER);
  }
  if (roleHasPermission(userRole, PERMISSIONS.INVITE_THERAPIST)) {
    options.push(USER_ROLES.THERAPIST);
  }
  
  return options;
};

// =============================================================================
// CLEAN ROLE SYSTEM - NO LEGACY CODE
// =============================================================================
